// ================================================================
// src/components/StudentResultsPage.js
//
// STUDENT - MY EXAM RESULTS
//
// Result_Display_Mode:
//
// HIDDEN
//   -> result is not displayed
//
// SUMMARY
//   -> score / percentage / status displayed
//   -> question-wise result is NOT available
//
// FULL
//   -> score / percentage / status displayed
//   -> "View Result" button displayed
//   -> complete question-wise result available
//
// IMPORTANT:
// This page reads the result visibility from the backend.
// It does NOT maintain its own HIDDEN/FULL setting.
//
// Backend source:
//     GET /results/student/:userId
//
// ================================================================

import React, {
  useEffect,
  useState
} from "react";

import axios from "axios";

import {
  useNavigate
} from "react-router-dom";


// ================================================================
// COMPONENT
// ================================================================

const StudentResultsPage = () => {

  const navigate = useNavigate();


  // ------------------------------------------------------------
  // STATE
  // ------------------------------------------------------------

  const [backendUrl, setBackendUrl] =
    useState("");

  const [results, setResults] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");


  // ------------------------------------------------------------
  // STUDENT ID
  //
  // Existing LMS login stores the user ID in localStorage.
  // ------------------------------------------------------------

  const userId =
    localStorage.getItem("userId");


  // ============================================================
  // LOAD BACKEND URL
  // ============================================================

  useEffect(() => {

    const script =
      document.createElement("script");

    script.src = "/config.js";

    script.onload = () => {

      if (
        window.env &&
        window.env.BACKEND_URL
      ) {

        setBackendUrl(
          window.env.BACKEND_URL
        );

      } else {

        console.error(
          "❌ BACKEND_URL not found in config.js"
        );

        setError(
          "Unable to connect to the server."
        );

        setLoading(false);

      }

    };


    script.onerror = () => {

      console.error(
        "❌ Unable to load /config.js"
      );

      setError(
        "Unable to connect to the server."
      );

      setLoading(false);

    };


    document.body.appendChild(script);


    return () => {

      if (
        document.body.contains(script)
      ) {

        document.body.removeChild(
          script
        );

      }

    };

  }, []);


  // ============================================================
  // FETCH STUDENT RELEASED RESULTS
  // ============================================================

  useEffect(() => {

    if (
      !backendUrl ||
      !userId
    ) {

      return;

    }


    fetchResults();

  }, [
    backendUrl,
    userId
  ]);


  // ============================================================
  // FETCH RESULTS
  // ============================================================

  const fetchResults = async () => {

    setLoading(true);

    setError("");


    try {

      console.log(
        `➡ GET /results/student/${userId}`
      );


      const response =
        await axios.get(
          `${backendUrl}/results/student/${userId}`
        );


      const data =
        Array.isArray(response.data)
          ? response.data
          : [];


      console.log(
        "📘 Student released results:",
        data
      );


      // --------------------------------------------------------
      // IMPORTANT
      //
      // Do not convert FULL to HIDDEN here.
      //
      // The backend has already read the database setting.
      // --------------------------------------------------------

      const normalizedResults =
        data.map((row) => {

          const mode =
            String(
              row.Result_Display_Mode ||
              row.resultDisplayMode ||
              row.displayMode ||
              "HIDDEN"
            )
              .trim()
              .toUpperCase();


          return {

            ...row,

            Result_Display_Mode:
              mode,

            // Backend normally returns Best_Attempt_ID.
            // Keep Attempt_ID as a fallback for compatibility.
            Best_Attempt_ID:
              row.Best_Attempt_ID ??
              row.Attempt_ID ??
              null

          };

        });


      console.log(
        "✅ Normalized student results:",
        normalizedResults
      );


      setResults(
        normalizedResults
      );


    } catch (err) {

      console.error(
        "❌ Error fetching student results:",
        err
      );


      setError(
        err.response?.data?.error ||
        "Unable to load your examination results."
      );


      setResults([]);

    } finally {

      setLoading(false);

    }

  };


  // ============================================================
  // VIEW FULL RESULT
  // ============================================================

  const handleViewResult = (
    result
  ) => {

    const mode =
      String(
        result.Result_Display_Mode ||
        ""
      )
        .trim()
        .toUpperCase();


    const attemptId =
      result.Best_Attempt_ID ??
      result.Attempt_ID ??
      null;


    console.log(
      "📘 View Result clicked:",
      {
        examId:
          result.Exam_ID,

        attemptId,

        displayMode:
          mode
      }
    );


    // ----------------------------------------------------------
    // FULL MODE ONLY
    // ----------------------------------------------------------

    if (
      mode !== "FULL"
    ) {

      alert(
        mode === "SUMMARY"
          ? "The instructor has released the summary only."
          : "The result has not been released."
      );

      return;

    }


    // ----------------------------------------------------------
    // ATTEMPT ID IS REQUIRED
    // ----------------------------------------------------------

    if (!attemptId) {

      alert(
        "The examination attempt could not be found."
      );

      console.error(
        "❌ FULL result has no attempt ID:",
        result
      );

      return;

    }


    // ----------------------------------------------------------
    // OPEN RESULT DETAILS
    //
    // Pass student identity to the backend so that the
    // detailed-result route can verify ownership.
    // ----------------------------------------------------------

    navigate(
      `/results/attempt/${attemptId}?viewerRole=STUDENT&userId=${userId}`
    );

  };


  // ============================================================
  // BACK
  // ============================================================

  const handleBack = () => {

    navigate(
      "/student/dashboard"
    );

  };


  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {

    return (

      <div
        style={pageContainer}
      >

        <h2
          style={sectionTitle}
        >
          My Exam Results
        </h2>


        <div
          style={messageBox}
        >
          Loading your examination results...
        </div>

      </div>

    );

  }


  // ============================================================
  // RENDER
  // ============================================================

  return (

    <div
      style={pageContainer}
    >

      {/* ======================================================
          TITLE
          ====================================================== */}

      <h2
        style={sectionTitle}
      >
        My Exam Results
      </h2>


      {/* ======================================================
          INFORMATION BOX
          ====================================================== */}

      <div
        style={infoBox}
      >

        <strong>
          Your released examination results are shown below.
        </strong>

        <div
          style={infoText}
        >
          Results will appear here only after the instructor
          releases them.
        </div>

      </div>


      {/* ======================================================
          ERROR
          ====================================================== */}

      {error && (

        <div
          style={errorBox}
        >
          {error}
        </div>

      )}


      {/* ======================================================
          RESULTS TABLE
          ====================================================== */}

      <div
        style={tableBox}
      >

        <table
          style={tableStyle}
        >

          <thead>

            <tr>

              <th style={thStyle}>
                Course
              </th>

              <th style={thStyle}>
                Exam
              </th>

              <th style={thStyle}>
                Attempt
              </th>

              <th style={thStyle}>
                Score
              </th>

              <th style={thStyle}>
                Percentage
              </th>

              <th style={thStyle}>
                Status
              </th>

              <th style={thStyle}>
                Action
              </th>

            </tr>

          </thead>


          <tbody>

            {results.length === 0 ? (

              <tr>

                <td
                  colSpan="7"
                  style={noDataStyle}
                >
                  No released examination results found.
                </td>

              </tr>

            ) : (

              results.map(
                (result, index) => {

                  const mode =
                    String(
                      result.Result_Display_Mode ||
                      ""
                    )
                      .trim()
                      .toUpperCase();


                  const attemptId =
                    result.Best_Attempt_ID ??
                    result.Attempt_ID ??
                    null;


                  const isFull =
                    mode === "FULL";


                  const isSummary =
                    mode === "SUMMARY";


                  return (

                    <tr
                      key={
                        result.Exam_ID
                          ? `${result.Exam_ID}-${index}`
                          : index
                      }
                    >

                      {/* COURSE */}

                      <td
                        style={tdStyle}
                      >
                        {result.Course_Name ||
                          "-"}
                      </td>


                      {/* EXAM */}

                      <td
                        style={tdStyle}
                      >
                        <strong>
                          {result.Exam_Name ||
                            "-"}
                        </strong>
                      </td>


                      {/* ATTEMPT */}

                      <td
                        style={tdStyle}
                      >
                        {result.Best_Attempt_Number != null
                          ? result.Best_Attempt_Number
                          : "-"}
                      </td>


                      {/* SCORE */}

                      <td
                        style={tdStyle}
                      >
                        {result.Best_Score != null
                          ? result.Best_Score
                          : "-"}
                      </td>


                      {/* PERCENTAGE */}

                      <td
                        style={tdStyle}
                      >
                        {result.Best_Percentage != null
                          ? `${Number(
                              result.Best_Percentage
                            ).toFixed(2)}%`
                          : "-"}
                      </td>


                      {/* STATUS */}

                      <td
                        style={{
                          ...tdStyle,
                          fontWeight: 600,
                          color:
                            result.Result_Status ===
                            "Passed"
                              ? "#188038"
                              : result.Result_Status ===
                                "Failed"
                              ? "#C70013"
                              : "#555"
                        }}
                      >
                        {result.Result_Status ||
                          "-"}
                      </td>


                      {/* ACTION */}

                      <td
                        style={tdStyle}
                      >

                        {isFull &&
                        attemptId ? (

                          <button
                            onClick={() =>
                              handleViewResult(
                                result
                              )
                            }
                            style={
                              viewButton
                            }
                          >
                            View Result
                          </button>

                        ) : isSummary ? (

                          <span
                            style={
                              summaryText
                            }
                          >
                            Summary Only
                          </span>

                        ) : (

                          <span>
                            -
                          </span>

                        )}

                      </td>

                    </tr>

                  );

                }
              )

            )}

          </tbody>

        </table>

      </div>

    </div>

  );

};


// ================================================================
// STYLES
// ================================================================

const pageContainer = {

  maxWidth: "1100px",

  margin:
    "20px auto",

  padding:
    "10px 20px",

  fontFamily:
    "Inter, Arial, sans-serif"

};


const sectionTitle = {

  color:
    "#C70013",

  fontSize:
    "26px",

  fontWeight:
    700,

  marginTop:
    "45px",

  marginBottom:
    "18px"

};


const backButton = {

  padding:
    "8px 16px",

  backgroundColor:
    "#666",

  color:
    "#fff",

  border:
    "none",

  borderRadius:
    "5px",

  cursor:
    "pointer",

  fontWeight:
    600

};


const infoBox = {

  backgroundColor:
    "#f7f7f7",

  border:
    "1px solid #ddd",

  borderRadius:
    "8px",

  padding:
    "16px",

  marginBottom:
    "30px",

  fontSize:
    "14px"

};


const infoText = {

  marginTop:
    "10px",

  color:
    "#333"

};


const messageBox = {

  padding:
    "20px",

  backgroundColor:
    "#f7f7f7",

  border:
    "1px solid #ddd",

  borderRadius:
    "8px",

  color:
    "#555"

};


const errorBox = {

  padding:
    "12px 15px",

  marginBottom:
    "20px",

  backgroundColor:
    "#fff1f1",

  border:
    "1px solid #e0a0a0",

  borderRadius:
    "6px",

  color:
    "#C70013",

  fontWeight:
    600

};


const tableBox = {

  backgroundColor:
    "#fff",

  borderRadius:
    "10px",

  padding:
    "10px",

  boxShadow:
    "0 3px 15px rgba(0,0,0,0.08)",

  overflowX:
    "auto"

};


const tableStyle = {

  width:
    "100%",

  borderCollapse:
    "collapse",

  minWidth:
    "850px"

};


const thStyle = {

  backgroundColor:
    "#EFEFEF",

  padding:
    "12px 10px",

  borderBottom:
    "2px solid #ccc",

  textAlign:
    "left",

  fontWeight:
    700

};


const tdStyle = {

  padding:
    "11px 10px",

  borderBottom:
    "1px solid #ddd",

  verticalAlign:
    "middle"

};


const noDataStyle = {

  padding:
    "25px",

  textAlign:
    "center",

  color:
    "#999",

  fontStyle:
    "italic"

};


const viewButton = {

  backgroundColor:
    "#C70013",

  color:
    "#fff",

  border:
    "none",

  padding:
    "7px 14px",

  borderRadius:
    "5px",

  cursor:
    "pointer",

  fontWeight:
    600,

  whiteSpace:
    "nowrap"

};


const summaryText = {

  color:
    "#666",

  fontSize:
    "13px",

  fontStyle:
    "italic"

};


export default StudentResultsPage;